import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { Pill, Dumbbell, Fish, Moon, Sun, Sparkles, Zap, Coffee, ShoppingBag, ArrowUpRight } from "lucide-react";

const ICONS = { pill: Pill, dumbbell: Dumbbell, fish: Fish, moon: Moon, sun: Sun, sparkles: Sparkles, zap: Zap, coffee: Coffee };
const AGE_LABEL = { "18-30": "18 a 30 años", "31-45": "31 a 45 años", "46+": "46 años en adelante" };

export default function Supplements() {
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/content/supplements").then((r) => setData(r.data));
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  if (!data) return <AppShell title="Suplementos por edad"><div className="text-[var(--mep-text-muted)]">Cargando...</div></AppShell>;

  const userBucket = profile ? (profile.age <= 30 ? "18-30" : profile.age <= 45 ? "31-45" : "46+") : null;

  return (
    <AppShell title="Suplementos por edad" subtitle="Guía general. Esta información es educativa y no sustituye la consulta médica.">
      <div className="space-y-10">
        {Object.keys(data).map((bucket) => (
          <section key={bucket}>
            <div className="flex items-center gap-3 mb-5">
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight">{AGE_LABEL[bucket]}</h2>
              {userBucket === bucket && <span className="mep-pill-pink">Tu rango</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data[bucket].map((s, idx) => {
                const Icon = ICONS[s.icon] || Pill;
                return (
                  <article key={idx} className="mep-card p-5 flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-[rgba(212,175,55,0.12)] border border-[var(--mep-gold)]/40 flex-shrink-0 flex items-center justify-center text-[var(--mep-gold)]">
                      <Icon size={20} />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-medium tracking-tight">{s.name}</h3>
                      <p className="text-sm text-[var(--mep-text-muted)] mt-1">{s.purpose}</p>
                      <p className="text-xs text-[var(--mep-pink)] mt-2 uppercase tracking-wider">{s.dose}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        {/* Shop CTA — externa */}
        <section
          data-testid="supplements-shop-cta"
          className="relative overflow-hidden rounded-2xl border border-[var(--mep-gold)]/40 bg-gradient-to-br from-[#1a1115] via-[#0f0a0c] to-[#13090e] p-7 md:p-10"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[rgba(232,61,132,0.10)] blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[rgba(212,175,55,0.08)] blur-3xl pointer-events-none" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)]">
              <ShoppingBag size={26} />
            </div>

            <div className="flex-1">
              <div className="text-eyebrow text-[var(--mep-gold)] mb-2">Tienda recomendada</div>
              <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight mb-3">
                Compra tus suplementos recomendados
              </h2>
              <p className="text-sm md:text-base text-[var(--mep-text-muted)] leading-relaxed max-w-2xl">
                Encuentra proteínas, creatina, omega 3, magnesio, colágeno, matcha y otros suplementos recomendados por la app para apoyar tus objetivos fitness.
              </p>
            </div>

            <a
              href="https://beacons.ai/yulifitstudio/suplementosyaccesoriosdegymusa"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="supplements-shop-cta-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm tracking-wide bg-gradient-to-r from-[var(--mep-pink)] to-[var(--mep-gold)] text-black hover:shadow-[0_0_30px_rgba(232,61,132,0.35)] hover:-translate-y-0.5 transition-all whitespace-nowrap"
            >
              Ver suplementos recomendados
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
          </div>
        </section>

        {/* Cuba supplements CTA — additional, non-destructive */}
        <section
          data-testid="supplements-cuba-cta-section"
          className="mep-card p-7 md:p-8 border border-[var(--mep-gold)]/25 bg-[rgba(212,175,55,0.04)]"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <div className="w-12 h-12 flex-shrink-0 rounded-full bg-[rgba(212,175,55,0.14)] border border-[var(--mep-gold)]/40 flex items-center justify-center text-[var(--mep-gold)]">
              <ShoppingBag size={22} />
            </div>
            <div className="flex-1">
              <div className="text-eyebrow text-[var(--mep-gold)] mb-2">🇨🇺 Cuba</div>
              <h3 className="font-display text-xl md:text-2xl font-light tracking-tight mb-2">
                ¿Estás en Cuba? Consulta suplementos disponibles
              </h3>
              <p className="text-sm text-[var(--mep-text-muted)] leading-relaxed max-w-2xl">
                Pide información por WhatsApp para conocer qué suplementos hay disponibles y cómo comprarlos localmente.
              </p>
            </div>
            <a
              href={`https://wa.me/5358697465?text=${encodeURIComponent("Hola, vengo de Mi Entrenador Personal y quiero información para comprar suplementos.")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="supplements-cuba-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm tracking-wide border border-[var(--mep-gold)]/50 text-[var(--mep-gold)] hover:bg-[rgba(212,175,55,0.12)] hover:border-[var(--mep-gold)] transition-all whitespace-nowrap"
            >
              🛒 Comprar suplementos en CUBA
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
